import { useRef, type ReactNode } from 'react';
import { useLogImpression } from '@archipro-website/tracker';
import { RoccoLink } from '@rocco/components/link';
import { cn } from '@rocco/utils/cn';
import { TextTooltip } from '~/ui-rocco/components/TextTooltip';
import {
    CategoryPills,
    type CategoryPill,
} from '~/ui-rocco/pages/home-page/components/CategoryPills';
import { CONTENT_CAP_PADDED } from './large-screen';
import { ClampedTagPills } from './ClampedTagPills';

type Theme = 'light' | 'dark';

export interface DetailCredit {
    name: string;
    /** Linked names go somewhere — a profile; unlinked ones are just stated. */
    link?: string;
    /** Called when a linked name is pressed — the page's tracking for it. */
    onClick?: () => void;
    /**
     * The credited professional, for the impression the name logs once it has
     * been in view: the same `LibraryItem_Directory` impression a professional
     * tile logs. Omit it for names with no profile behind them.
     */
    impression?: { itemID: number; professionalID: number };
    /** What they did — Architect, Photographer — shown on hover. */
    role?: string;
    /**
     * A face beside the name — the article's author. The picture when there
     * is one, their initials in the disc when there is not.
     */
    avatar?: { src?: string; initials: string };
}

/** The disc beside a credited name: their picture, or their initials. */
const CreditAvatar = ({
    avatar,
    theme,
}: {
    avatar: NonNullable<DetailCredit['avatar']>;
    theme: Theme;
}) => (
    <span
        aria-hidden="true"
        className={cn(
            'inline-flex size-[2rem] shrink-0 items-center justify-center overflow-hidden rounded-full font-sans text-[0.6875rem] leading-none font-medium tracking-[0.02em] uppercase',
            theme === 'dark'
                ? 'bg-white/10 text-white'
                : 'bg-[#e4e4e6] text-[#323232]',
        )}
    >
        {avatar.src ? (
            <img
                src={avatar.src}
                alt=""
                loading="lazy"
                className="size-full object-cover"
            />
        ) : (
            avatar.initials
        )}
    </span>
);

/** One fact of a details column: "Range  Japanese Collection". */
export interface DetailCreditPair {
    label: string;
    /** Beside the label; with `valuesBelow`, the values as one line of text. */
    value: string;
    link?: string;
    /** Runs as the reader follows `link`, for click tracking. */
    onClick?: () => void;
    /**
     * Values set under the label, one a line, in place of `value` beside it:
     * a profile's associations, whose names run longer than a fact's.
     */
    valuesBelow?: { value: string; link?: string }[];
}

/** A pair's value: a link where it goes somewhere, its words where not. */
const PairValue = ({
    value,
    link,
    onClick,
    theme,
}: {
    value: string;
    link?: string;
    onClick?: () => void;
    theme: Theme;
}) =>
    link ? (
        <RoccoLink
            to={link}
            onClick={onClick}
            className={cn(
                entryClass(theme),
                'transition-opacity hover:opacity-70',
            )}
        >
            {value}
        </RoccoLink>
    ) : (
        <span className={entryClass(theme)}>{value}</span>
    );

export interface DetailCreditColumn {
    label: string;
    entries: DetailCredit[];
    /**
     * Label/value pairs in place of a list of names — the product's range,
     * brand, designer and year under one "Product Details" head. When set,
     * `entries` is ignored for content but still decides the column's
     * presence, so pass the pairs as entries too or leave entries empty
     * and let `pairs` speak.
     */
    pairs?: DetailCreditPair[];
    /**
     * The pairs as a table — labels in one column, values lined up in the
     * next — rather than each value following its label. For a longer run of
     * facts read down the page, as the product's Product Details are.
     */
    pairsAsTable?: boolean;
    /**
     * Each pair on one line, the pairs in a single column as wide as the
     * longest, rather than wrapping in the column's width or splitting in two.
     */
    pairsOnOneLine?: boolean;
    /**
     * How many columns the names run in under the one label. A long list — a
     * house can carry twenty suppliers — otherwise runs off down the page on
     * its own while the row beside it has finished, so it takes two.
     */
    columns?: 1 | 2;
    /**
     * A rendered column in place of names or pairs — the product's colour
     * swatches, its material and size pickers. When set it is the column's
     * whole content under the label, and its presence.
     */
    node?: ReactNode;
    /**
     * Set at the right of the names, centred on their block — the article
     * page's share marks beside its author. Names only; a column drawn as
     * pairs or a node lays itself out.
     */
    trailing?: ReactNode;
}

export interface DetailCreditsRowProps {
    /** Outline pills under a "Tags" label; the column is omitted when empty. */
    tags?: CategoryPill[];
    /** Set the pills without their "Tags" label below md. */
    hideMobileTagsLabel?: boolean;
    /**
     * The most lines of tags shown; past them a last pill says how many more
     * there are and shows them when pressed. Unset shows every tag.
     */
    maxTagLines?: number;
    /** Columns of names after the tags; each hides when empty. */
    columns: DetailCreditColumn[];
    /**
     * Columns set on the edge the copy beside the picture below starts on
     * — the product's variants, which line up with the description under
     * them. With these the row splits where the picture ends: the tags and
     * `columns` over the picture, these over the copy.
     */
    alignedColumns?: DetailCreditColumn[];
    /**
     * A column set last, on the copy's side — the product's "Specified by".
     * With it, the aligned columns stack in one column beside it rather than
     * running across.
     */
    trailingColumn?: DetailCreditColumn;
    /**
     * With a trailing column, the aligned columns become one list under this
     * label: each column's name as a pair's label, what it offers beside it.
     */
    alignedLabel?: string;
    theme?: Theme;
    /** The row's ground, for a page whose background is not white. */
    className?: string;
}

// "Tags" / "Project team" labels — Figma 2354:55678 is SF Pro Semibold (590);
// font-sans (Neue Haas) has no 600 face, so use the real 500 cut (matches the
// intro caption), 15px, tracking -0.05em.
const labelClass = (theme: Theme) =>
    cn(
        'm-0 font-sans font-medium tracking-[-0.05em] max-md:text-sm md:text-[0.9375rem]',
        theme === 'dark' ? 'text-[#9a9a9a]' : 'text-[#767676]',
    );

// 12px uppercase per Figma (node 2354:55689).
const entryClass = (theme: Theme) =>
    cn(
        'font-sans leading-normal uppercase max-md:text-[0.75rem] md:text-[0.75rem]',
        theme === 'dark' ? 'text-white' : 'text-[#181818]',
    );

/**
 * One credited name. A linked name logs `impression` once it has been in view,
 * and runs `onClick` as it is followed.
 */
const CreditEntry = ({
    entry,
    theme,
}: {
    entry: DetailCredit;
    theme: Theme;
}) => {
    const ref = useRef<HTMLLIElement>(null);
    useLogImpression({
        data: entry.impression && {
            type: 'LibraryItem_Directory',
            ...entry.impression,
        },
        ref,
    });

    return (
        <li
            ref={ref}
            className={cn(entry.avatar && 'flex items-center gap-[0.75rem]')}
        >
            {entry.avatar ? (
                <CreditAvatar avatar={entry.avatar} theme={theme} />
            ) : null}
            {/* The role rides on hover: the list names who, and the tooltip
                says what they did. */}
            <TextTooltip content={entry.role ?? ''}>
                {entry.link ? (
                    <RoccoLink
                        to={entry.link}
                        onClick={entry.onClick}
                        className={cn(
                            entryClass(theme),
                            'transition-opacity hover:opacity-70',
                        )}
                    >
                        {entry.name}
                    </RoccoLink>
                ) : (
                    <span className={entryClass(theme)}>{entry.name}</span>
                )}
            </TextTooltip>
        </li>
    );
};

/** A pair's label: the names' 12px capitals, a step back from the value. */
const pairLabelClass = (theme: Theme) =>
    cn(
        'font-sans leading-normal uppercase max-md:text-[0.75rem] md:text-[0.75rem]',
        // Muted, but past 4.5:1 on the product page's #f5f5f7 ground (AA for
        // text this size).
        theme === 'dark' ? 'text-white/50' : 'text-[#6b6b6b]',
    );

/**
 * The table's grid tracks for a desktop row of two or three pairs, each pair
 * its label then the value it fills. One pair per row is the base — the
 * mobile setting, and the desktop one for a table sharing the row — so only
 * the wider settings carry a class. Written out rather than repeated from a
 * constant: Tailwind reads these class names out of the source.
 */
const TABLE_TRACKS: Record<number, string> = {
    1: '',
    2: 'md:grid-cols-[max-content_minmax(0,1fr)_max-content_minmax(0,1fr)]',
    3: 'md:grid-cols-[max-content_minmax(0,1fr)_max-content_minmax(0,1fr)_max-content_minmax(0,1fr)]',
};

/**
 * A column of label/value pairs: the label a step back from the value, both
 * at the names' 12px, four to a column and flowing into a second past that.
 */
const PairsColumn = ({
    column,
    theme,
    tableColumns = 1,
}: {
    column: DetailCreditColumn & { pairs: DetailCreditPair[] };
    theme: Theme;
    /** Pairs across a desktop row of the table — see `tableColumnsFor`. */
    tableColumns?: number;
}) => {
    const {
        label,
        pairs,
        pairsAsTable = false,
        pairsOnOneLine = false,
    } = column;
    if (pairs.length === 0) return null;

    const wide = pairs.length > 4 && !pairsAsTable && !pairsOnOneLine;

    return (
        <div
            className={cn(
                'flex flex-col gap-3 md:gap-[1.5625rem]',
                pairsAsTable
                    ? 'min-w-0 md:flex-1'
                    : pairsOnOneLine
                      ? 'md:w-max md:shrink-0'
                      : wide
                        ? 'md:w-[41rem]'
                        : 'md:w-[19.8rem]',
            )}
        >
            <p className={labelClass(theme)}>{label}</p>
            <dl
                className={cn(
                    'm-0 p-0',
                    pairsAsTable
                        ? 'grid grid-cols-[max-content_minmax(0,1fr)] gap-x-[1.5rem] gap-y-[0.25rem] [&>div]:contents'
                        : 'flex flex-col gap-[0.25rem]',
                    pairsAsTable && TABLE_TRACKS[tableColumns],
                    wide &&
                        'md:block md:columns-2 md:gap-[1.4rem] [&>div]:break-inside-avoid',
                )}
            >
                {pairs.map((pair) => (
                    <div
                        key={pair.label}
                        className={cn(
                            pair.valuesBelow
                                ? 'flex flex-col gap-[0.25rem]'
                                : 'flex items-baseline gap-[0.5rem]',
                            pairsOnOneLine && 'md:whitespace-nowrap',
                        )}
                    >
                        <dt className={pairLabelClass(theme)}>{pair.label}</dt>
                        {pair.valuesBelow ? (
                            pair.valuesBelow.map((below) => (
                                <dd key={below.value} className="m-0">
                                    <PairValue {...below} theme={theme} />
                                </dd>
                            ))
                        ) : (
                            <dd className="m-0">
                                <PairValue
                                    value={pair.value}
                                    link={pair.link}
                                    onClick={pair.onClick}
                                    theme={theme}
                                />
                            </dd>
                        )}
                    </div>
                ))}
            </dl>
        </div>
    );
};

/** A column whose content is a component under the label. */
const NodeColumn = ({
    column,
    theme,
    className,
}: {
    column: DetailCreditColumn & { node: ReactNode };
    theme: Theme;
    className?: string;
}) => (
    <div
        className={cn(
            'flex flex-col gap-3 md:w-[19.8rem] md:gap-[1.5625rem]',
            className,
        )}
    >
        <p className={labelClass(theme)}>{column.label}</p>
        {column.node}
    </div>
);

const NameColumn = ({
    column,
    theme,
}: {
    column: DetailCreditColumn;
    theme: Theme;
}) => {
    const { label, entries, columns = 1, trailing } = column;
    if (entries.length === 0) return null;

    const wide = columns === 2;

    return (
        <div
            className={cn(
                'flex flex-col gap-3 md:gap-[1.5625rem]',
                // Two of the single column's width, plus the gap between them,
                // so the pair sits on the same rhythm as the columns beside it.
                wide ? 'md:w-[41rem]' : 'md:w-[19.8rem]',
            )}
        >
            <p className={labelClass(theme)}>{label}</p>
            {trailing != null ? (
                <div className="flex items-center justify-between gap-4">
                    <ul
                        className={cn(
                            'm-0 list-none p-0',
                            // Multi-column rather than two lists: the browser balances
                            // the names across them, so neither side is left short.
                            wide
                                ? 'md:columns-2 md:gap-[1.4rem] [&>li]:break-inside-avoid'
                                : '',
                            'flex flex-col gap-0',
                            wide && 'md:block',
                        )}
                    >
                        {entries.map((entry) => (
                            <CreditEntry
                                key={entry.name}
                                entry={entry}
                                theme={theme}
                            />
                        ))}
                    </ul>
                    {trailing}
                </div>
            ) : (
                <ul
                    className={cn(
                        'm-0 list-none p-0',
                        // Multi-column rather than two lists: the browser balances
                        // the names across them, so neither side is left short.
                        wide
                            ? 'md:columns-2 md:gap-[1.4rem] [&>li]:break-inside-avoid'
                            : '',
                        'flex flex-col gap-0',
                        wide && 'md:block',
                    )}
                >
                    {entries.map((entry) => (
                        <CreditEntry
                            key={entry.name}
                            entry={entry}
                            theme={theme}
                        />
                    ))}
                </ul>
            )}
        </div>
    );
};

/**
 * The meta row under a detail page's summary: a "Tags" column of outline
 * pills beside columns of credited names — the project's team and suppliers,
 * the product's colours, materials and sizes, the article's professional and
 * author. Each column hides when empty; the row hides when none has content.
 */
const hasContent = (column: DetailCreditColumn) =>
    column.entries.length > 0 ||
    (column.pairs?.length ?? 0) > 0 ||
    column.node != null;

/**
 * One column, drawn by what it carries: a component, pairs, or names. Also
 * drawn on its own, where a page sets the credits somewhere other than the
 * row — the project page's phone description carries them behind Read more.
 */
export const DetailCreditColumnBlock = ({
    column,
    theme,
    nodeClassName,
    tableColumns,
}: {
    column: DetailCreditColumn;
    theme: Theme;
    nodeClassName?: string;
    tableColumns?: number;
}) =>
    column.node != null ? (
        <NodeColumn
            column={{ ...column, node: column.node }}
            theme={theme}
            className={nodeClassName}
        />
    ) : column.pairs ? (
        <PairsColumn
            column={{ ...column, pairs: column.pairs }}
            theme={theme}
            tableColumns={tableColumns}
        />
    ) : (
        <NameColumn column={column} theme={theme} />
    );

/**
 * Columns as one list under a label: each column's name set as a pair's
 * label, and its names or its component beside it, as Product Details sets
 * its facts.
 */
const AlignedList = ({
    label,
    columns,
    theme,
}: {
    label: string;
    columns: DetailCreditColumn[];
    theme: Theme;
}) => (
    <div className="flex min-w-0 flex-col gap-3 md:gap-[1.5625rem]">
        <p className={labelClass(theme)}>{label}</p>
        <dl className="m-0 grid grid-cols-[max-content_minmax(0,1fr)] items-start gap-x-[1.5rem] gap-y-[1rem] p-0">
            {columns.map((column) => (
                <div key={column.label} className="contents">
                    <dt className={pairLabelClass(theme)}>{column.label}</dt>
                    <dd className="m-0 min-w-0">
                        {column.node ??
                            column.entries.map((entry) => (
                                <span
                                    key={entry.name}
                                    className={cn(entryClass(theme), 'block')}
                                >
                                    {entry.name}
                                </span>
                            ))}
                    </dd>
                </div>
            ))}
        </dl>
    </div>
);

/** Below this a split leaves a stub column rather than a shorter table. */
const MIN_PAIRS_PER_TABLE_COLUMN = 4;

/**
 * How many pairs a table of facts sets across a desktop row, from what else
 * is on the row beside it.
 *
 * A product with tags and variants leaves the table a third of the width and
 * one column is all that fits. A product with neither — no search tags, a
 * single variant — leaves it the row's whole width, where the same single
 * column reads as a thin list of labels against an empty half. So the table
 * takes the width the row is not using: three pairs across when it is alone,
 * two when only the tags sit beside it, and never so many that a column is
 * left holding fewer than four facts.
 */
const tableColumnsFor = (
    pairCount: number,
    { hasTags, hasAligned }: { hasTags: boolean; hasAligned: boolean },
) => {
    if (hasAligned) return 1;
    const roomFor = hasTags ? 2 : 3;
    const filled = Math.floor(pairCount / MIN_PAIRS_PER_TABLE_COLUMN);
    return Math.min(roomFor, Math.max(1, filled));
};

export const DetailCreditsRow = ({
    tags = [],
    hideMobileTagsLabel = false,
    maxTagLines,
    columns,
    alignedColumns = [],
    trailingColumn,
    alignedLabel = '',
    theme = 'light',
    className,
}: DetailCreditsRowProps) => {
    const trailing =
        trailingColumn && hasContent(trailingColumn) ? trailingColumn : null;
    const hasColumns = [...columns, ...alignedColumns].some(hasContent);
    if (tags.length === 0 && !hasColumns && !trailing) return null;
    const aligned = alignedColumns.filter(hasContent);
    // The copy's side of the row: the aligned columns, and the trailing one.
    const hasSide = aligned.length > 0 || trailing != null;
    const tagPills = {
        items: tags,
        className: theme === 'dark' ? 'text-white' : 'text-[#181818]',
        // The homepage's vw-scaled pill gap is ~1px on a phone (pills touch);
        // give the tags real breathing room and let them wrap to a second line.
        wrapClassName: 'max-md:gap-x-2 max-md:gap-y-2.5',
        // Held to the column: a tag is free text, and one long enough ran out
        // of the column and across the names beside it.
        pillClassName: 'inline-block max-w-full truncate',
    };

    return (
        <section
            className={cn(
                'max-md:px-6 md:px-[3rem]',
                theme === 'dark' ? 'bg-transparent' : 'bg-ground',
                className,
            )}
        >
            <div
                className={cn(
                    CONTENT_CAP_PADDED,
                    // Split where the picture below ends when some columns are
                    // set from it: 41.95% of the width, the detail pages'
                    // picture, then the copy's column.
                    hasSide
                        ? 'max-md:flex max-md:flex-col max-md:gap-8 md:grid md:grid-cols-[41.95%_minmax(0,1fr)]'
                        : 'flex max-md:flex-col max-md:gap-8 md:gap-[9rem]',
                )}
            >
                <div
                    className={cn(
                        hasSide
                            ? 'flex min-w-0 max-md:flex-col max-md:gap-8 md:gap-[9rem]'
                            : 'contents',
                    )}
                >
                    {tags.length > 0 ? (
                        <div className="flex shrink-0 flex-col gap-3 md:w-[19.8rem] md:gap-[1.5625rem]">
                            <p
                                className={cn(
                                    labelClass(theme),
                                    hideMobileTagsLabel && 'max-md:hidden',
                                )}
                            >
                                Tags
                            </p>
                            {maxTagLines ? (
                                <ClampedTagPills
                                    {...tagPills}
                                    maxLines={maxTagLines}
                                />
                            ) : (
                                <CategoryPills {...tagPills} />
                            )}
                        </div>
                    ) : null}
                    {columns.map((column) => (
                        <DetailCreditColumnBlock
                            key={column.label}
                            column={column}
                            theme={theme}
                            tableColumns={tableColumnsFor(
                                column.pairs?.length ?? 0,
                                {
                                    hasTags: tags.length > 0,
                                    hasAligned: hasSide,
                                },
                            )}
                        />
                    ))}
                </div>
                {trailing ? (
                    // The aligned columns one under another, then the
                    // trailing one beside them.
                    <div className="grid min-w-0 gap-8 max-md:grid-cols-1 md:grid-cols-2 md:gap-[1.5rem] md:pl-[6.25rem]">
                        {aligned.length > 0 ? (
                            <AlignedList
                                label={alignedLabel}
                                columns={aligned}
                                theme={theme}
                            />
                        ) : null}
                        <DetailCreditColumnBlock
                            column={trailing}
                            theme={theme}
                            nodeClassName="md:w-auto"
                        />
                    </div>
                ) : aligned.length > 0 ? (
                    // The picture's 100px gap, so the first of these starts
                    // where the copy beside the picture does.
                    <div className="grid min-w-0 gap-8 max-md:grid-cols-1 md:grid-cols-3 md:gap-[1.5rem] md:pl-[6.25rem]">
                        {aligned.map((column) => (
                            <DetailCreditColumnBlock
                                key={column.label}
                                column={column}
                                theme={theme}
                                nodeClassName="md:w-auto"
                            />
                        ))}
                    </div>
                ) : null}
            </div>
        </section>
    );
};
